// ===========================================================================
// Names for rooms Lumi creates by itself.
//
// Every auto-created room was called "Random room", which tells you nothing and
// makes a directory of them unreadable -- three rooms with the same name is
// three rooms you cannot tell apart. A room called Pizza Party is somewhere you
// might want to go.
//
// Rules the list follows, because a name is the first thing a stranger sees and
// both age bands share it:
//
//   * Nothing suggestive, nothing about drink or drugs, nothing that implies an
//     age or a place to meet. A MINORS room draws from this same list.
//   * Nothing that could read as an official Lumi channel -- no "Support", no
//     "Help Desk" -- for the same reason handles are screened for it.
//   * Warm and a bit silly rather than clever. The point is to feel like a
//     door somebody left open.
//
// ASCII only, so no punctuation that needs escaping and nothing that renders as
// a box in the wrong font.
// ===========================================================================

export const ROOM_NAMES = Object.freeze([
  'Pizza Party',
  'The Blanket Fort',
  'Midnight Snack Club',
  'Cereal Enthusiasts',
  'The Waffle House',
  'Pancake Summit',
  'Toast Appreciation',
  'The Cookie Jar',
  'Soup Season',
  'Sandwich Debate Club',
  'The Popcorn Bucket',
  'Ice Cream Weather',
  'The Tea Room',
  'Hot Chocolate Hour',
  'The Biscuit Tin',
  'Second Breakfast',
  'The Lemonade Stand',
  'Marshmallow Campfire',
  'The Pretzel Twist',
  'Doughnut Circle',

  'Couch Potatoes',
  'The Comfy Chair',
  'Slipper Society',
  'The Nap Room',
  'Pillow Fight',
  'The Hammock',
  'Lazy Sunday',
  'The Window Seat',
  'Rainy Day Club',
  'Sock Drawer',
  'The Beanbag',
  'Duvet Day',

  'Cat Video Club',
  'The Dog Park',
  'Duck Pond',
  'Penguin Parade',
  'The Otter Pool',
  'Hedgehog Corner',
  'Alpaca Farm',
  'The Frog Chorus',
  'Bee Garden',
  'Turtle Speed',
  'The Owl Tree',
  'Red Panda Club',
  'Capybara Lounge',
  'The Squirrel Stash',

  'Paper Airplanes',
  'The Sticker Book',
  'Doodle Page',
  'Origami Corner',
  'The Puzzle Table',
  'Crossword Club',
  'Sock Puppet Theatre',
  'The Cardboard Castle',
  'Bubble Wrap',
  'The Kite Field',
  'Chalk Drawings',
  'The Sandcastle',

  'Rainy Window',
  'Sunbeam Corner',
  'The Garden Shed',
  'Wildflower Field',
  'The Treehouse',
  'Autumn Leaves',
  'First Snow',
  'The Lighthouse',
  'Rock Pool',
  'The Greenhouse',
  'Mushroom Circle',
  'The Orchard',
  'Cloud Watching',
  'The Meadow',

  'Mixtape Club',
  'The Record Player',
  'Humming Along',
  'Kazoo Orchestra',
  'The Whistle Stop',
  'Rubber Duck Debugging',
  'The Slow Lane',
  'Left Sock Mystery',
  'The Lost and Found',
  'Wrong Answers Only',
  'The Long Way Round',
  'Mild Peril',
  'The Scenic Route',
  'Overthinking It',
  'The Group Project',
  'Procrastination Station',

  'Star Map',
  'The Rocket Shed',
  'Moon Landing',
  'Comet Tail',
  'The Observatory',
  'Space Camp',
  'Orbit Lounge',
  'The Satellite',

  'Board Game Night',
  'The Dice Tower',
  'Checkmate Club',
  'The Card Table',
  'Domino Line',
  'Marble Run',
  'The Pinball Machine',
  'High Score',

  'The Front Porch',
  'Corner Shop',
  'The Bus Stop',
  'Library Voices',
  'The Bookshelf',
  'Museum Bench',
  'The Bike Rack',
  'Laundry Day',
]);

/**
 * A name for a new room, avoiding the ones already in use.
 *
 * Picks at random from whatever is free so a quiet site does not hand out the
 * same first name every time. When every name is taken it numbers them --
 * "Pizza Party 2", then 3 -- rather than falling back to something meaningless:
 * the list running out should degrade into a slightly duller name, never into a
 * failure or a wall of random characters.
 *
 * @param {Iterable<string>} takenNames names currently in use
 * @param {() => number} rnd injectable for tests
 */
export function pickRoomName(takenNames = [], rnd = Math.random) {
  const taken = new Set();
  for (const n of takenNames) if (typeof n === 'string') taken.add(n.trim().toLowerCase());

  const free = ROOM_NAMES.filter((n) => !taken.has(n.toLowerCase()));
  if (free.length) return free[Math.floor(rnd() * free.length) % free.length];

  // Everything is in use. Number them, lowest free suffix first, so the second
  // Pizza Party is "Pizza Party 2" and not "Pizza Party 47".
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const candidates = ROOM_NAMES.map((n) => `${n} ${suffix}`).filter((n) => !taken.has(n.toLowerCase()));
    if (candidates.length) return candidates[Math.floor(rnd() * candidates.length) % candidates.length];
  }
  // Beyond a hundred thousand simultaneous rooms this is not the problem to
  // solve, but it must still return something usable.
  return `Room ${Math.floor(rnd() * 1e6).toString(36)}`;
}

export default { ROOM_NAMES, pickRoomName };
